import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { AuthTitleBlock, ConfirmButton, MainLayout } from 'src/components';
import { PAGES } from 'src/constants/pages';
import { clearAuthToken, signOutUserHelper } from 'src/helpers';
import { setUserRole, signOutUser } from 'src/redux/actions/authActions';
import styles from './ChooseAccountTypePage.module.scss';
import { accountTypeFields } from './constants';
import cn from 'classnames';
import InfoTextBlock from 'src/atoms/InfoTextBlock';
import { useUserValues } from 'src/hooks';
import { ACCOUNT_ROLES } from 'src/constants/globalConstants';
import { redirectHandler } from './helpers';

const ChooseAccountTypePage = (): JSX.Element => {
  const { push } = useHistory();
  const dispatch = useDispatch();

  const { companyName, profileName, userRole, email } = useUserValues();

  const [accountType, setAccountType] = useState<ACCOUNT_ROLES>(ACCOUNT_ROLES.USER);

  const onSelectTypeClick = (type: ACCOUNT_ROLES) => setAccountType(type);

  const handleLogOut = async () => {
    try {
      await signOutUserHelper();
      clearAuthToken();
      dispatch(signOutUser());
      push(PAGES.SIGN_IN);
    } catch (error) {
      console.error('handleLogOut error: ', error);
    }
  };

  const onConfirm = useCallback(async () => {
    dispatch(setUserRole(accountType));

    await redirectHandler(accountType, email, push);
  }, [accountType, dispatch, email, push]);

  const description = useMemo(() => {
    const names = {
      [ACCOUNT_ROLES.COMPANY]: companyName || 'Company Account',
      [ACCOUNT_ROLES.USER]: profileName || 'User Account'
    };
    const name = names[accountType];

    return `You will be logged in as a ${name}.`;
  }, [accountType, companyName, profileName]);

  if (userRole !== ACCOUNT_ROLES.MULTIUSER) return <Redirect to={PAGES.SIGN_IN} />;

  return (
    <MainLayout isCentered isPrivate={false} isFixedHeader>
      <AuthTitleBlock
        title="How would you like to proceed?"
        button={{ buttonLabel: 'Log Out', onClick: handleLogOut }}
        classes={{
          blockClass: styles.titleBlock,
          linkClass: styles.logOutLink
        }}
      />

      <div className={styles.selectTypeWrapper}>
        {accountTypeFields.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => onSelectTypeClick(key)}
            type="button"
            className={cn(styles.selectTypeBtn, { [styles.selectTypeBtnActive]: accountType === key })}
          >
            {label}
          </button>
        ))}
      </div>

      <InfoTextBlock classes={{ wrapper: styles.descriptionWrapper }}>{description}</InfoTextBlock>

      <ConfirmButton type="button" style={{ margin: '0 auto' }} onClick={onConfirm}>
        Continue
      </ConfirmButton>
    </MainLayout>
  );
};

export default ChooseAccountTypePage;
